// Audience endpoints the report's Audience layer uses — the same `/audiences/…` routes the
// Audiences module calls, wrapped here so the report doesn't depend on that module's slice (and
// never writes to its store). Thin promise wrappers resolving the raw response, like
// ../intelligenceReportApi.js; `API_HOST` already supplies /api/v1 and arClient adds the Bearer
// token + tenant headers.

import arClient from '../../../util/api_client';

const apiGet = (endpoint) => new Promise((resolve) => arClient.doApiCallGet(endpoint, resolve));

// doApiCallV2 posts by default; PATCH goes through its `method` option.
const apiSend = (endpoint, body = {}, options = {}) =>
  new Promise((resolve) => arClient.doApiCallV2(endpoint, body, resolve, options));

const audiencePath = (audienceId) => `/audiences/${encodeURIComponent(String(audienceId))}`;
const segmentPath = (audienceId, segmentId) =>
  `${audiencePath(audienceId)}/segments/${encodeURIComponent(String(segmentId))}`;

/** GET /audiences/{id}/segments — status + every segment (REST is the source of truth). */
export const getAudienceSegments = (audienceId) => apiGet(`${audiencePath(audienceId)}/segments`);

/** GET /audiences/{id}/summary — the Audience layer's summary card. */
export const getAudienceSummary = (audienceId) => apiGet(`${audiencePath(audienceId)}/summary`);

/**
 * PATCH /audiences/{id}/segments/selection — like ('Accept'), dislike ('Reject') or clear (neutral,
 * `status: null`) segments; segments not listed keep their vote. Ids are `segmentId` values, sent
 * as numbers; an empty list is left out. An id may appear in one list only (422 otherwise).
 */
export const saveSegmentSelection = (
  audienceId,
  likedSegmentIds = [],
  dislikedSegmentIds = [],
  neutralSegmentIds = []
) => {
  const body = {};
  if (likedSegmentIds.length) body.likedSegmentIds = likedSegmentIds.map(Number);
  if (dislikedSegmentIds.length) body.dislikedSegmentIds = dislikedSegmentIds.map(Number);
  if (neutralSegmentIds.length) body.neutralSegmentIds = neutralSegmentIds.map(Number);
  return apiSend(`${audiencePath(audienceId)}/segments/selection`, body, { method: 'PATCH' });
};

/**
 * POST /audiences/{id}/segments/generate-more — `feedback` (optional, ≤ 5000 chars) and the
 * segmentId arrays: liked (reference), disliked (avoided) and replaced (archived by the backend,
 * vote unchanged). Empty parts are left out; the backend doesn't infer "the rest".
 */
export const generateMoreSegments = (
  audienceId,
  { feedback, likedSegmentIds = [], dislikedSegmentIds = [], replacedSegmentIds = [] } = {}
) => {
  const body = {};
  if (feedback) body.feedback = feedback;
  if (likedSegmentIds.length) body.likedSegmentIds = likedSegmentIds.map(Number);
  if (dislikedSegmentIds.length) body.dislikedSegmentIds = dislikedSegmentIds.map(Number);
  if (replacedSegmentIds.length) body.replacedSegmentIds = replacedSegmentIds.map(Number);
  return apiSend(`${audiencePath(audienceId)}/segments/generate-more`, body);
};

/** GET /audiences/{a}/segments/{s}/influencers — `limit` / `offset` narrow the list. */
export const getSegmentInfluencers = (audienceId, segmentId, { limit, offset } = {}) => {
  const query = new URLSearchParams();
  if (limit != null) query.set('limit', String(limit));
  if (offset != null) query.set('offset', String(offset));
  const qs = query.toString();
  return apiGet(`${segmentPath(audienceId, segmentId)}/influencers${qs ? `?${qs}` : ''}`);
};

/**
 * POST /audiences/{a}/segments/{s}/influencers/archive — archive (`archived: true`) or restore one
 * influencer, identified by `username` (the row's handle) + `platform`.
 */
export const setSegmentInfluencerArchived = (
  audienceId,
  segmentId,
  { username, platform, archived }
) =>
  apiSend(`${segmentPath(audienceId, segmentId)}/influencers/archive`, {
    username,
    platform,
    archived,
  });
